import { useState, useEffect, useCallback } from 'react'
import questionnaireConfigFallback from '../config/questionnaire.json'

/**
 * Custom hook for loading questionnaire from API with fallback to local JSON
 * @param {string} version - Specific version to load (optional, defaults to active)
 * @returns {Object} - Questionnaire data, loading state, error, and refetch function
 */
export const useQuestionnaire = (version = null) => {
  const [questionnaire, setQuestionnaire] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchQuestionnaire = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Determine endpoint based on whether we're loading a specific version
      const endpoint = version 
        ? `/api/questionnaire/${version}`
        : '/api/questionnaire/active'

      const response = await fetch(endpoint)

      if (!response.ok) {
        throw new Error(`Failed to fetch questionnaire: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to load questionnaire')
      }

      // Set questionnaire from API response, including version metadata
      const questionnaireData = data.data.questionnaire || data.data
      const questionnaireWithVersion = {
        ...questionnaireData,
        version: data.data.version || questionnaireData.version
      }
      
      setQuestionnaire(questionnaireWithVersion)
      setLoading(false)

    } catch (err) {
      console.warn('⚠️ Failed to load questionnaire from API, using local fallback:', err.message)

      // Fallback to local JSON file
      setQuestionnaire(questionnaireConfigFallback.questionnaire)
      setError(err.message)
      setLoading(false)
    }
  }, [version])

  useEffect(() => {
    fetchQuestionnaire()
  }, [fetchQuestionnaire])

  return {
    questionnaire,
    loading,
    error,
    refetch: fetchQuestionnaire
  }
}

/**
 * Custom hook for loading all questionnaire versions (admin only)
 * @returns {Object} - Versions array, loading state, error, and refetch function
 */
export const useQuestionnaireVersions = () => {
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchVersions = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const token = localStorage.getItem('adminToken')
      if (!token) {
        throw new Error('Authentication required')
      }

      const response = await fetch('/api/questionnaire/versions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch versions: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to load versions')
      }

      setVersions(data.data.versions || [])
      setLoading(false)

    } catch (err) {
      console.error('❌ Failed to load questionnaire versions:', err.message)
      setError(err.message)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchVersions()
  }, [fetchVersions])

  return {
    versions,
    loading,
    error,
    refetch: fetchVersions
  }
}

/**
 * Custom hook for questionnaire management operations (admin only)
 * @returns {Object} - Management functions
 */
export const useQuestionnaireManagement = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken')
    if (!token) {
      throw new Error('Authentication required')
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  }

  const createVersion = async (questionnaireData, metadata) => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch('/api/questionnaire', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          questionnaire: questionnaireData,
          metadata
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to create version')
      }

      setLoading(false)
      return data.data

    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }

  const activateVersion = async (version) => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(`/api/questionnaire/${encodeURIComponent(version)}/activate`, {
        method: 'PUT',
        headers: getAuthHeaders()
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to activate version')
      }

      setLoading(false)
      return true

    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }

  const deleteVersion = async (version) => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(`/api/questionnaire/${encodeURIComponent(version)}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to delete version')
      }

      setLoading(false)
      return true

    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }

  const compareVersions = async (v1, v2) => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(`/api/questionnaire/compare/${encodeURIComponent(v1)}/${encodeURIComponent(v2)}`, {
        headers: getAuthHeaders()
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to compare versions')
      }

      setLoading(false)
      return data.data

    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }

  return {
    createVersion,
    activateVersion,
    deleteVersion,
    compareVersions,
    loading,
    error
  }
}

export default useQuestionnaire

